// Runtime feature-flag hook for the native apps.
//
// • Fetches /api/app/flags at first render and then every 5 minutes
// • Caches the result in localStorage so first render after a cold start
//   uses the last-known value (no UI flicker)
// • Falls back to safe defaults if the backend is unreachable
//
// The admin can flip flags from AdminPage → "Feature Flags" tab.
// Currently exposed flags:
//   • bonus_ios_hidden — hide Bonus bottom-nav tab on iOS builds
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'rv_feature_flags_v1';
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

const DEFAULTS = {
  bonus_ios_hidden: false,
};

const readCached = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULTS;
  }
};

export const useFeatureFlags = () => {
  const [flags, setFlags] = useState(readCached);

  useEffect(() => {
    let mounted = true;
    let interval = null;

    const fetchFlags = async () => {
      try {
        const { data } = await axios.get(`${API}/app/flags`, { timeout: 5000 });
        if (!mounted) return;
        const next = { ...DEFAULTS, ...(data?.flags || {}) };
        setFlags(next);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      } catch {
        // Network/backend down: keep using cached/default flags (don't crash)
      }
    };

    fetchFlags();
    interval = setInterval(fetchFlags, REFRESH_INTERVAL_MS);

    return () => {
      mounted = false;
      if (interval) clearInterval(interval);
    };
  }, []);

  return flags;
};

export default useFeatureFlags;
